import { Injectable, Provider, inject, signal } from '@angular/core';

import { TpvEntityFocus, parseTpvEntityFocus } from '@app/domain/models/tpv/tpv-entity-focus.model';
import { TpvEntityFocusStore } from '@app/domain/ports/tpv/tpv-entity-focus.store';
import { BrowserStorageService } from '@app/services/browser-storage.service';

/** Llave de `sessionStorage` del foco TPV. */
export const TPV_ENTITY_FOCUS_KEY = 'tpv.entityFocus';

/**
 * El foco TPV en `sessionStorage` (aislamiento por pestaña, igual que
 * `tpv.selectedMerchant`), detrás de una `signal` para que el dashboard y las
 * migas reaccionen al cambio.
 *
 * Se guarda SOLO `{level, externalId}` (R21): ningún nombre. Al leer, cualquier
 * forma inesperada se descarta como "sin foco".
 *
 * ⚠️ Es un singleton raíz: la `signal` se inicializa UNA vez desde el storage y
 * cerrar sesión NO recarga la página, así que borrar `sessionStorage` no basta
 * (la signal seguiría con el foco del usuario anterior). Lo vacía
 * `LogoutService.forgetTabMemory()` llamando a `clear()`, que limpia signal Y
 * storage (cierre de sesión e inicio de login).
 */
@Injectable({ providedIn: 'root' })
export class SessionTpvEntityFocusStore extends TpvEntityFocusStore {
  private readonly storage = inject(BrowserStorageService);
  private readonly actual = signal<TpvEntityFocus | null>(this.restaurar());

  focus(): TpvEntityFocus | null {
    return this.actual();
  }

  set(focus: TpvEntityFocus): void {
    const valido = parseTpvEntityFocus(focus);
    if (!valido) {
      this.clear();
      return;
    }
    this.actual.set(valido);
    // Se serializa el objeto RECONSTRUIDO, no el recibido: aunque alguien pase
    // campos de más, a storage solo llegan nivel e id.
    this.storage.setSessionStorageItem(
      TPV_ENTITY_FOCUS_KEY,
      JSON.stringify({ level: valido.level, externalId: valido.externalId }),
    );
  }

  clear(): void {
    this.actual.set(null);
    this.storage.removeSessionStorageItem(TPV_ENTITY_FOCUS_KEY);
  }

  private restaurar(): TpvEntityFocus | null {
    const crudo = this.storage.getSessionStorageItem(TPV_ENTITY_FOCUS_KEY);
    if (!crudo) return null;
    try {
      return parseTpvEntityFocus(JSON.parse(crudo));
    } catch {
      // Valor manipulado o corrupto: sin foco (R21). No se registra el contenido.
      return null;
    }
  }
}

/** Enlace del puerto. Va en la raíz (`app.config.ts`). */
export function provideTpvEntityFocusStore(): Provider[] {
  return [{ provide: TpvEntityFocusStore, useExisting: SessionTpvEntityFocusStore }];
}
